/**
 * Downscale + re-encode images before they are held in memory or uploaded.
 *
 * Why: large camera/gallery photos (8–20 MB) kept as File/base64 in form state
 * raise the WebView's heap and make Android more likely to kill a backgrounded
 * PWA. Compressing at pick time keeps memory and upload payloads small.
 */

export type CompressImageOptions = {
  /** Longest edge in px; image is scaled down to fit. */
  maxDimension?: number;
  /** JPEG/WebP quality 0–1. */
  quality?: number;
  mimeType?: "image/jpeg" | "image/webp";
};

const DEFAULTS = {
  maxDimension: 1920,
  quality: 0.82,
  mimeType: "image/jpeg" as const,
};

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
}

async function loadBitmap(
  file: File,
): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    try {
      // `from-image` bakes EXIF orientation into the pixels.
      return await createImageBitmap(file, {imageOrientation: "from-image"});
    } catch {
      // fall through to <img> decode
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = "async";
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function getDimensions(source: ImageBitmap | HTMLImageElement) {
  if ("width" in source && "height" in source) {
    const width =
      source instanceof HTMLImageElement ? source.naturalWidth : source.width;
    const height =
      source instanceof HTMLImageElement ? source.naturalHeight : source.height;
    return {width, height};
  }
  return {width: 0, height: 0};
}

function replaceExtension(name: string, mimeType: string) {
  const ext = mimeType === "image/webp" ? "webp" : "jpg";
  const base = name.replace(/\.[^.]+$/, "");
  return `${base || "image"}.${ext}`;
}

/**
 * Returns a compressed copy of the image File, or the original file if it is
 * not a (static) raster image or if compression fails / yields no gain.
 */
export async function compressImageFile(
  file: File,
  options: CompressImageOptions = {},
): Promise<File> {
  if (!file.type.startsWith("image/") || file.type === "image/gif") {
    return file;
  }

  const {maxDimension, quality, mimeType} = {...DEFAULTS, ...options};

  try {
    const source = await loadBitmap(file);
    const {width, height} = getDimensions(source);
    if (!width || !height) {
      return file;
    }

    const scale = Math.min(1, maxDimension / Math.max(width, height));
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return file;
    }
    ctx.drawImage(source, 0, 0, targetWidth, targetHeight);

    if ("close" in source && typeof source.close === "function") {
      source.close();
    }

    const blob = await canvasToBlob(canvas, mimeType, quality);
    if (!blob) {
      return file;
    }

    // No downscale and re-encoding did not help — keep the original.
    if (scale === 1 && blob.size >= file.size) {
      return file;
    }

    return new File([blob], replaceExtension(file.name, mimeType), {
      type: mimeType,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}

/**
 * Capture a still frame from a live <video> element into a downscaled JPEG File.
 * Used by the in-page getUserMedia camera (no native camera Activity → the
 * WebView never backgrounds, so Android does not kill the PWA).
 */
export async function captureVideoFrameToFile(
  video: HTMLVideoElement,
  options: CompressImageOptions & {fileName?: string} = {},
): Promise<File | null> {
  const {maxDimension, quality, mimeType, fileName} = {...DEFAULTS, ...options};

  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) {
    return null;
  }

  const scale = Math.min(1, maxDimension / Math.max(width, height));
  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  ctx.drawImage(video, 0, 0, targetWidth, targetHeight);

  const blob = await canvasToBlob(canvas, mimeType, quality);
  if (!blob) {
    return null;
  }

  const name = fileName ?? `photo-${Date.now()}.${mimeType === "image/webp" ? "webp" : "jpg"}`;
  return new File([blob], name, {type: mimeType, lastModified: Date.now()});
}
